// ---------------------------------------------------------------------------
// why-now-flight - the timeline and path maths for the card-dots-become-graph
// assembly, translated from the working-memory film's graph.js:
//
//   node = { pre, home, flight }  ->  pos = home + rot(pre - home, e) * (1 - e)
//
// Every card dot is a real simulation node pinned at `pre` (its position inside
// its card). At its flight time the pin is animated toward the graph and the
// remaining offset rotates coherently as it shrinks - the film's spiral infall -
// before the pin is dropped and the link force takes the node over. Same object
// throughout; nothing is ever faded out and re-created somewhere else.
// ---------------------------------------------------------------------------

import type { Point } from './why-now-scene';

/** The root tile lands before anything is allowed to attach to it. */
export const ROOT_POP_MS = 320;
/** Film cadence: (cardIndex * 5 + dotIndex) * 42ms, here BFS index * 42ms. */
export const LAUNCH_STAGGER_MS = 42;
export const FLIGHT_MS = 1150;
/** An edge draws while its child is still closing on the parent. */
export const EDGE_DELAY_MS = 620;
export const EDGE_FADE_MS = 260;
export const NODE_FADE_MS = 260;
export const RETURN_MS = 350;
export const RETURN_FADE_MS = 300;
export const BOX_FADE_MS = 500;
/** The cards stay sharp for the first stretch of the scrub - long enough to be read - before any of them dissolves. */
export const CARD_HOLD = 0.34;
/** Scrub window in which a card's dots resolve out of its dissolving mock UI. */
export const DOT_FADE_START = 0.52;
export const DOT_FADE_SPAN = 0.2;
export const CARD_STAGGER = 0.05;
export const DOT_STAGGER = 0.02;

export const clamp01 = (value: number) => Math.min(1, Math.max(0, value));

export const easeInOut = (t: number) =>
	t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;

export const easeOutBack = (t: number) => {
	const c1 = 1.70158;
	const c3 = c1 + 1;
	return 1 + c3 * (t - 1) ** 3 + c1 * (t - 1) ** 2;
};

/** Deterministic 0..1 per node - the film's per-node `seed`. */
export const hashSeed = (id: string) => {
	let hash = 0;
	for (let i = 0; i < id.length; i += 1) hash = (hash * 31 + id.charCodeAt(i)) | 0;
	return ((hash >>> 8) & 1023) / 1023;
};

export const flightStartMs = (bfsIndex: number) =>
	ROOT_POP_MS + bfsIndex * LAUNCH_STAGGER_MS;

export const edgeBornMs = (bfsIndex: number) => flightStartMs(bfsIndex) + EDGE_DELAY_MS;

/**
 * Beat 3 never waits on the assembly: reached mid-flight, the flights still in
 * the air finish on a faster clock, all landed this long after the beat starts.
 */
export const ASSEMBLY_RUSH_MS = 900;

/** A clock that runs in real time up to `at`, then `rate` times faster. */
export interface ClockRush {
	at: number;
	rate: number;
}

export const rushedElapsed = (startedAt: number, now: number, rush?: ClockRush) =>
	!rush || now <= rush.at ? now - startedAt : rush.at - startedAt + (now - rush.at) * rush.rate;

/** Alpha of a card dot during the scrubbed dissolve of its card. */
export const dotScrubAlpha = (progress: number, cardIndex: number, dotIndex: number) => {
	const start = DOT_FADE_START + cardIndex * CARD_STAGGER + dotIndex * DOT_STAGGER;
	return clamp01((progress - start) / DOT_FADE_SPAN);
};

/**
 * The film's flight path: the leftover offset to the target rotates by
 * `e * (0.55 + 0.45 * seed)` while it shrinks, so the whole field spirals in
 * rather than sliding along five straight lines.
 */
export const spiralTo = (pre: Point, target: Point, eased: number, seed: number): Point => {
	const offsetX = pre.x - target.x;
	const offsetY = pre.y - target.y;
	const theta = eased * (0.55 + 0.45 * seed);
	const cos = Math.cos(theta);
	const sin = Math.sin(theta);
	const remaining = 1 - eased;
	return {
		x: target.x + (offsetX * cos - offsetY * sin) * remaining,
		y: target.y + (offsetX * sin + offsetY * cos) * remaining,
	};
};

/**
 * Where a region head sits on the ring round the root. The offset turns the
 * ring so the four regions read right / bottom / left / top - a region boxed
 * from a diagonal slice would put its top-left title straight on the Naumu tile.
 */
const HEAD_RING_OFFSET = -(50 / 180) * Math.PI;

export const headAngle = (index: number, count: number) =>
	((index + 0.5) / Math.max(1, count)) * Math.PI * 2 + HEAD_RING_OFFSET;
